import type { RegisterableHotkey } from "@tanstack/hotkeys";
import type { MaybeRefOrGetter } from "vue";
import type { ShortcutPlatform } from "@/utils/shortcuts";
import { useHotkey } from "@tanstack/vue-hotkeys";
import { unref } from "vue";
import { isShortcutOverlayOpen } from "@/composables/shortcut-overlay";
import { detectShortcutPlatform, normalizeShortcutKeys } from "@/utils/shortcuts";

export type ShortcutKeys = string | string[];

export interface UseShortcutOptions {
  /** Whether the shortcut is active. Reactive. */
  enabled?: MaybeRefOrGetter<boolean>;
  /** DOM target to attach the listener to. Defaults to `document`. */
  target?: MaybeRefOrGetter<HTMLElement | Document | Window | null>;
  /**
   * Ignore events originating from editable fields (input, textarea, select,
   * contenteditable). Defaults to `true` so ordinary global actions do not fire
   * while the user is typing.
   */
  ignoreInputs?: boolean;
  /** Prevent the browser default action once the shortcut is accepted. Defaults to `true`. */
  preventDefault?: boolean;
  /** Stop event propagation once the shortcut is accepted. Defaults to `true`. */
  stopPropagation?: boolean;
  /** Allow OS key-repeat to keep invoking the handler. Defaults to `false`. */
  allowRepeat?: boolean;
  /** Require every key to be released before the handler can fire again. */
  requireReset?: boolean;
  /**
   * Keep firing while a shortcut overlay (the command palette) is open. Only the
   * overlay's own controls should set this.
   */
  overlayOwned?: boolean;
  /** Platform used to resolve `Mod`, for both handling and hints. */
  platform?: ShortcutPlatform;
}

function resolveHotkey(hotkey: MaybeRefOrGetter<ShortcutKeys>): RegisterableHotkey {
  const value = typeof hotkey === "function" ? (hotkey as () => ShortcutKeys)() : unref(hotkey);
  return normalizeShortcutKeys(value) as RegisterableHotkey;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // jsdom does not implement `isContentEditable`, so fall back to the attribute.
  if (target.isContentEditable) return true;
  if (target.getAttribute("contenteditable") !== null) return true;
  if (target.closest("[contenteditable]")) return true;

  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

function isComposingEvent(event: KeyboardEvent): boolean {
  // `Process` is the standard key value for IME input (legacy keyCode 229).
  return event.isComposing || event.key === "Process";
}

/**
 * Thin application wrapper around TanStack `useHotkey`.
 *
 * The library owns matching and cleanup. This wrapper only fixes app defaults
 * (editable protection, repeat/composition, overlay ownership) and applies
 * `preventDefault` / `stopPropagation` *after* every guard accepts the action,
 * so rejected events stay untouched for the focused field or overlay.
 */
export function useShortcut(
  hotkey: MaybeRefOrGetter<ShortcutKeys>,
  handler: (event: KeyboardEvent) => void,
  options: UseShortcutOptions = {}
): void {
  const platform = options.platform ?? detectShortcutPlatform();
  const ignoreInputs = options.ignoreInputs ?? true;

  useHotkey(
    () => resolveHotkey(hotkey),
    (event) => {
      // Reka/TanStack only guard input/textarea/select; contenteditable needs
      // the same protection for ordinary global actions.
      if (ignoreInputs && isEditableTarget(event.target)) return;
      // IME composition Enter must not submit or be consumed.
      if (isComposingEvent(event)) return;
      // An open overlay owns the keyboard; only its own shortcuts may fire.
      if (isShortcutOverlayOpen.value && !options.overlayOwned) return;
      if (!options.allowRepeat && event.repeat) return;

      if (options.preventDefault ?? true) event.preventDefault();
      if (options.stopPropagation ?? true) event.stopPropagation();
      handler(event);
    },
    {
      enabled: options.enabled,
      target: options.target,
      // Editable protection is app-owned so contenteditable is covered too.
      ignoreInputs: false,
      // Applied inside the callback after the guards, never by the library.
      preventDefault: false,
      stopPropagation: false,
      requireReset: options.requireReset,
      platform,
      conflictBehavior: "allow",
    }
  );
}
